import React, { Component } from 'react';
import { connect } from 'react-redux';
import { isEmpty, reject } from 'lodash/fp';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import ModalContainer from 'components/shared/modal/modal_container';
import RenderInBody from 'components/shared/render_in_body';
import elementsActions from 'actions/editor/elements';
import { Icon, Spinner } from 'ui';
import ZendeskLibraryItem from './ZendeskLibraryItem';
import ZendeskLibraryActions from './ZendeskLibraryActions';
import ZendeskLibrarySearch from './ZendeskLibrarySearch';
import ZendeskLibraryFilter from './ZendeskLibraryFilter';

class ZendeskLibrary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      isLoading: true,
      selectedItem: null,
      error: '',
      query: '',
    };

    this.handleCancel = this.handleCancel.bind(this);
    this.renderBody = this.renderBody.bind(this);
    this.renderEmptyState = this.renderEmptyState.bind(this);
    this.handleItemClick = this.handleItemClick.bind(this);
    this.currentSelectedItem = this.currentSelectedItem.bind(this);
    this.searchData = this.searchData.bind(this);
    this.updateQuery = this.updateQuery.bind(this);
    this.clearSearchInput = this.clearSearchInput.bind(this);
    this.filterData = this.filterData.bind(this);
    this.deleteItem = this.deleteItem.bind(this);
    this.handleAddToLesson = this.handleAddToLesson.bind(this);
  }

  componentDidMount() {
    this.requestData();
  }

  componentDidUpdate() {
    this.setContainerHeight();
  }

  setContainerHeight() {
    const container = document.querySelector('.ElementLibrary-container');
    const containerCalculation = this.state.error ? 196 : 140;

    if (container) {
      const maxHeight =
        window.innerHeight -
        container.getBoundingClientRect().top -
        containerCalculation;

      container.style.maxHeight = maxHeight + 'px';
    }
  }

  requestData(filter) {
    request
      .get(routes.ZENDESK_TICKETS, { filter })
      .then(data => {
        this.setState({ data, isLoading: false, error: '' });
      })
      .fail(() => {
        this.setState({
          data: [],
          isLoading: false,
          error: this.props.i18n.error.no_connection,
        });
      });
  }

  filterData(e) {
    this.setState({ isLoading: true, selectedItem: null });
    this.requestData(e.target.value);
  }

  currentSelectedItem(item) {
    return item === this.state.selectedItem;
  }

  handleItemClick(item) {
    if (this.currentSelectedItem(item)) {
      this.setState({ selectedItem: null, error: '' });
    } else {
      this.setState({ selectedItem: item, error: '' });
    }
  }

  deleteItem(item) {
    this.setState(state => ({
      data: reject({ id: item.id }, state.data),
    }));
    request.delete(routes.ZENDESK_TICKETS + '/' + item.id).fail(() => {
      this.setState(state => ({
        data: state.data.concat(item),
        error: this.props.i18n.error.on_delete,
      }));
    });
  }

  searchData(content) {
    const query = new RegExp(this.state.query, 'i');
    return (
      query.test(content.subject) ||
      query.test(content.description) ||
      query.test(content.creator_provided_description) ||
      query.test(content.zendesk_id) ||
      query.test(content.creator_name)
    );
  }

  updateQuery(e) {
    this.setState({ query: e.target.value });
  }

  clearSearchInput() {
    this.setState({ query: '' });
  }

  handleCancel() {
    this.props.closeZendeskLibrary();
  }

  handleAddToLesson() {
    const { position } = this.props;
    const zendeskTicketId = this.state.selectedItem.id;
    const contentType = 'Questions::TicketHandling';

    this.props
      .createZendeskTicket(contentType, position, zendeskTicketId)
      .then(element => {
        this.props.closeZendeskLibrary();
      })
      .catch(() => {
        this.setState({ error: this.props.i18n.error.on_save });
      });
  }

  get buttonConfig() {
    return {
      i18n: this.props.i18n.actions,
      handleCancel: this.handleCancel,
      selectedItem: this.state.selectedItem,
      handleAddToLesson: this.handleAddToLesson,
    };
  }

  renderEmptyState() {
    return (
      <div className="ElementLibrary-message">
        {this.state.error && (
          <div className="ErrorContainer ElementLibrary-errorMessage lh-24">
            {this.state.error}
          </div>
        )}
        {!this.state.error && this.props.i18n.no_content}
      </div>
    );
  }

  renderBody() {
    const filteredData = this.state.data.filter(this.searchData);
    const hasData = !isEmpty(filteredData);

    if (this.state.query && !hasData) {
      return (
        <div className="ElementLibrary-message">
          {this.props.i18n.no_results}
        </div>
      );
    }
    if (!hasData) {
      return this.renderEmptyState();
    }

    return filteredData.map(item => (
      <ZendeskLibraryItem
        item={item}
        key={item.zendesk_id}
        onClick={this.handleItemClick}
        active={this.currentSelectedItem(item)}
        deleteItem={this.deleteItem}
        currentUserId={this.props.currentUserId}
        i18n={this.props.i18n}
      />
    ));
  }

  render() {
    const { header, loading } = this.props.i18n;
    const { isLoading } = this.state;
    const hasData = !isEmpty(this.state.data);

    return (
      <RenderInBody>
        <div className="ElementLibrary">
          <ModalContainer
            className="ModalTakeover-background Modal-background"
            onEscape={this.handleCancel}
          >
            <div className="ElementLibrary-body ModalTakeover-container d-f fd-c">
              <div
                role="button"
                tabIndex={0}
                aria-label={t('common.close')}
                className="ModalTakeover-closeButton"
                onClick={this.handleCancel}
                onKeyDown={e => e.key === 'Enter' && this.handleCancel()}
              >
                <Icon name="error" />
              </div>
              <div className="ElementLibrary-heading mb-32">
                <div className="ElementLibrary-header">{header.title}</div>
                <div className="ElementLibrary-instructions">
                  {header.instructions}
                </div>
              </div>
              <ZendeskLibrarySearch
                i18n={this.props.i18n}
                search={this.updateQuery}
                value={this.state.query}
                clearSearchInput={this.clearSearchInput}
              />
              <ZendeskLibraryFilter
                filterData={this.filterData}
                i18n={this.props.i18n}
              />
              <div className="ElementLibrary-container">
                <div className="ElementLibrary-content">
                  {isLoading && (
                    <div className="ElementLibrary-message">
                      <div>
                        <Spinner color="darker" />
                        <span className="ml-8">{loading}</span>
                      </div>
                    </div>
                  )}
                  {!isLoading && !hasData && this.renderEmptyState()}
                  {!isLoading && hasData && this.renderBody()}
                </div>
              </div>
              <ZendeskLibraryActions {...this.buttonConfig} />
            </div>
          </ModalContainer>
        </div>
      </RenderInBody>
    );
  }
}

const mapStateToProps = ({ editor }) => ({
  currentUserId: editor.currentUserId,
  i18n: editor.i18n.practice.zendesk,
  lessonId: editor.lessonId,
  sectionId: editor.currentSection.id,
});

const mapDispatchToProps = dispatch => ({
  createZendeskTicket(contentType, position, zendeskTicketId) {
    return dispatch(
      elementsActions.createZendeskTicket(
        contentType,
        position,
        zendeskTicketId,
      ),
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ZendeskLibrary);
